import React, { createContext, useContext, useEffect, useState } from 'react'
import { Product } from '@/lib/data'
import { gtmEvents } from '@/lib/gtm'
import { toast } from 'sonner'

export interface CartItem extends Product {
  quantity: number
  size?: string
  color?: string
}

interface CartContextType {
  items: CartItem[]
  addToCart: (
    product: Product,
    quantity: number,
    size?: string,
    color?: string,
  ) => void
  removeFromCart: (itemId: string) => void
  updateQuantity: (itemId: string, delta: number) => void
  clearCart: () => void
  cartTotal: number
  itemsCount: number
}

const CartContext = createContext<CartContextType | undefined>(undefined)

export const CartProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [items, setItems] = useState<CartItem[]>(() => {
    const saved = localStorage.getItem('cart')
    return saved ? JSON.parse(saved) : []
  })

  useEffect(() => {
    localStorage.setItem('cart', JSON.stringify(items))
  }, [items])

  const cartTotal = items.reduce(
    (acc, item) => acc + item.price * item.quantity,
    0,
  )
  const itemsCount = items.reduce((acc, item) => acc + item.quantity, 0)

  const addToCart = (
    product: Product,
    quantity: number,
    size?: string,
    color?: string,
  ) => {
    setItems((prev) => {
      const existing = prev.find(
        (i) => i.id === product.id && i.size === size && i.color === color,
      )
      if (existing) {
        return prev.map((i) =>
          i.id === product.id && i.size === size && i.color === color
            ? { ...i, quantity: i.quantity + quantity }
            : i,
        )
      }
      return [...prev, { ...product, quantity, size, color }]
    })

    const addedItem = {
      item_id: product.id,
      item_name: product.name,
      price: product.price,
      quantity: quantity,
      item_category: product.category,
      item_variant: size ? `${size}/${color}` : undefined,
    }

    gtmEvents.addToCart(addedItem)
    toast.success('Produto adicionado ao carrinho!')
  }

  const removeFromCart = (itemId: string) => {
    const itemToRemove = items.find((i) => i.id === itemId)
    if (itemToRemove) {
      const gtmItem = {
        item_id: itemToRemove.id,
        item_name: itemToRemove.name,
        price: itemToRemove.price,
        quantity: itemToRemove.quantity,
        item_category: itemToRemove.category,
        item_variant: itemToRemove.size
          ? `${itemToRemove.size}/${itemToRemove.color}`
          : undefined,
      }
      gtmEvents.removeFromCart(gtmItem)

      setItems((prev) => prev.filter((i) => i.id !== itemId))
      toast.info('Item removido do carrinho.')
    }
  }

  const updateQuantity = (itemId: string, delta: number) => {
    setItems((prev) =>
      prev.map((item) => {
        if (item.id === itemId) {
          const newQuantity = Math.max(1, item.quantity + delta)
          return { ...item, quantity: newQuantity }
        }
        return item
      }),
    )
  }

  const clearCart = () => {
    setItems([])
  }

  return (
    <CartContext.Provider
      value={{
        items,
        addToCart,
        removeFromCart,
        updateQuantity,
        clearCart,
        cartTotal,
        itemsCount,
      }}
    >
      {children}
    </CartContext.Provider>
  )
}

export const useCart = () => {
  const context = useContext(CartContext)
  if (!context) {
    throw new Error('useCart must be used within a CartProvider')
  }
  return context
}
